// src/hooks/useChangeParticipantPassword.ts
import { use, useCallback, useState } from 'react';
import { postAPI } from '../services/api.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

type UseChangeParticipantPasswordOpts = {
  userId: string;
  groupId:string;
  onSuccess?: (data?: any) => void;
};

export function useChangeParticipantPassword({ userId, groupId, onSuccess }: UseChangeParticipantPasswordOpts) {
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const navigate=useNavigate();

  const validate = useCallback(() => {
    const e: any = {};
    const pw = (newPassword || '').trim();
    const cpw = (confirmPassword || '').trim();

    if (!pw) e.newPassword = 'New password is required';
    else if (pw.length < 8) e.newPassword = 'Password must be at least 8 characters';
    // optional additional complexity rules — uncomment if you want
    // else if (!/[A-Z]/.test(pw)) e.newPassword = 'Include at least one uppercase letter';
    // else if (!/[a-z]/.test(pw)) e.newPassword = 'Include at least one lowercase letter';
    // else if (!/[0-9]/.test(pw)) e.newPassword = 'Include at least one digit';
    // else if (!/[^A-Za-z0-9]/.test(pw)) e.newPassword = 'Include at least one special character';

    if (!cpw) e.confirmPassword = 'Please confirm password';
    else if (pw && cpw && pw !== cpw) e.confirmPassword = 'Passwords do not match';

    setErrors(Object.keys(e).length ? e : null);
    return Object.keys(e).length === 0;
  }, [newPassword, confirmPassword]);

  const submit = useCallback(async () => {
    setServerError(null);
    setSuccessMsg(null);

    if (!validate()) return false;
    if (!userId) {
      setServerError('Missing userId');
      return false;
    }

    setLoading(true);
    try {
      const payload = { userId: String(userId), newPassword: newPassword.trim(),groupId };
      const resp = await postAPI<{ data?: any }>(
        'groups/change-participant-password', // baseURL in api.service includes /api/
        payload
      );

      // treat HTTP status 2xx as success
      if (resp.status >= 200 && resp.status < 300) {
        const message = (resp.data as any)?.message ?? 'Password changed successfully';
        setSuccessMsg(message);
        toast.success(message);
        setNewPassword('');
        setConfirmPassword('');
        if (onSuccess) onSuccess((resp.data as any)?.data ?? resp.data);
        setLoading(false);
navigate('/login');
        return true;
      } else {
        const errMsg = (resp as any)?.error || (resp.data as any)?.error || (resp.data as any)?.message || `Request failed (${resp.status})`;
        setServerError(String(errMsg));
        toast.error(String(errMsg));
        setLoading(false);
        return false;
      }
    } catch (err: any) {
      console.error('change-password error', err);
      const msg = err?.message || 'Network error';
      setServerError(msg);
      toast.error(msg);
      setLoading(false);
      return false;
    }
  }, [newPassword, userId, validate, onSuccess]);

  const reset = useCallback(() => {
    setNewPassword('');
    setConfirmPassword('');
    setErrors(null);
    setServerError(null);
    setSuccessMsg(null);
  }, []);

  return {
    // state
    newPassword,
    confirmPassword,
    loading,
    errors,
    serverError,
    successMsg,
    // actions
    setNewPassword,
    setConfirmPassword,
    submit,
    reset
  };
}
